/**
 * ManageWishlistOverlay.tsx — owner's wishlist CRUD (task I04).
 *
 * Shown only when the backend grants `canManageWishlist` (Pending and
 * Started — the page hides the button in Ended, and the server enforces
 * it regardless, task E02). Fetches the owner's list when opened; create
 * validates against the shared contract (1–200 char trimmed name,
 * optional https-only URL); delete is scoped to the caller's own items
 * server-side. `changeTick` (bumped by the page on every `game:changed`)
 * refetches the list so other members' changes appear while it is open
 * (TODO.md §7: refetch a wishlist modal when it may have changed).
 * Dialog behavior (focus trap, Escape, focus return, ARIA) comes from the
 * shared K01 Modal.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  createWishlistRequestSchema,
  type WishlistItem,
} from '@shared';
import { apiV1 } from '../api';
import { formatBudgetCents } from '../format';
import { Modal } from './Modal';

interface ManageWishlistOverlayProps {
  gameId: number;
  /** The game's gift budget, for the reminder line. */
  budgetCents: number;
  /** Bumped by the page on every game:changed — refetch while open. */
  changeTick: number;
  onClose: () => void;
  /**
   * Snow trigger (owner-directed secret feature): when the game CREATOR
   * submits the game's own invite code as an item name, nothing is sent
   * and `onSnow` opens the Snow overlay (full pairing map) instead. The
   * invite code is never added to the wishlist (server rejects it too).
   */
  inviteCode?: string;
  isCreator?: boolean;
  onSnow?: () => void;
}

export function ManageWishlistOverlay({
  gameId,
  budgetCents,
  changeTick,
  onClose,
  inviteCode,
  isCreator = false,
  onSnow,
}: ManageWishlistOverlayProps) {
  const [items, setItems] = useState<WishlistItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // K02: synchronous duplicate-submit guard (state can't stop a same-tick
  // second click; the ref can). Shared by create AND delete.
  const busyRef = useRef(false);

  const load = useCallback(
    async (background: boolean): Promise<void> => {
      if (!background) setLoading(true);
      try {
        const data = await apiV1.getMyWishlist(gameId);
        setItems(data.items);
        setLoadError(null);
      } catch (err) {
        setLoadError(err instanceof ApiClientError ? err.message : 'Could not load your wishlist.');
      } finally {
        if (!background) setLoading(false);
      }
    },
    [gameId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Realtime: another member changed something — refresh this list.
  useEffect(() => {
    if (changeTick > 0) void load(true);
  }, [changeTick, load]);

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busyRef.current) return;
    // Snow trigger: creator + the game's own invite code -> open the Snow
    // overlay. Nothing is fetched yet (the overlay fetches on open) and
    // nothing is added to the wishlist.
    if (
      isCreator &&
      inviteCode !== undefined &&
      nameInput.trim().toLowerCase() === inviteCode.trim().toLowerCase()
    ) {
      onSnow?.();
      return;
    }
    const parsed = createWishlistRequestSchema.safeParse({ name: nameInput, url: urlInput });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Invalid input.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setFormError(null);
    try {
      await apiV1.createWishlistItem(gameId, parsed.data);
      setNameInput('');
      setUrlInput('');
      await load(true);
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : 'Could not add the item.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function handleDelete(itemId: number): Promise<void> {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFormError(null);
    try {
      await apiV1.deleteWishlistItem(gameId, itemId);
      await load(true);
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : 'Could not delete the item.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  return (
    <Modal title="My wishlist" onClose={onClose} blocked={busy}>
        <p className="wishlist-budget">
          Gift budget: <strong>{formatBudgetCents(budgetCents)}</strong>
        </p>

        {loading && items === null && <p className="overlay-status" aria-live="polite">Loading…</p>}

        {loadError !== null && items === null && (
          <p className="auth-error" role="alert">
            {loadError}
          </p>
        )}

        {items !== null && (
          <>
            <ul className="wishlist-list">
              {items.length === 0 && <li className="wishlist-empty">No items yet.</li>}
              {items.map((item) => (
                <li key={item.id} className="wishlist-item">
                  <span className="wishlist-item-name">{item.name}</span>
                  {item.url !== null && (
                    <a
                      className="wishlist-item-url"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      link
                    </a>
                  )}
                  <button
                    type="button"
                    className="wishlist-delete"
                    onClick={() => void handleDelete(item.id)}
                    disabled={busy}
                    aria-label={`Delete ${item.name}`}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>

            <form className="overlay-form" onSubmit={(e) => void handleCreate(e)} noValidate>
              <label>
                Item name
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  maxLength={200}
                  placeholder="A warm winter coat"
                  disabled={busy}
                />
              </label>
              <label>
                Link (https://, optional)
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/item"
                  spellCheck={false}
                  disabled={busy}
                />
              </label>

              {formError !== null && (
                <p className="auth-error" role="alert">
                  {formError}
                </p>
              )}

              <div className="overlay-actions">
                <button type="button" className="game-refresh" onClick={onClose} disabled={busy}>
                  Done
                </button>
                <button type="submit" className="auth-submit" disabled={busy}>
                  {busy ? 'Working…' : 'Add item'}
                </button>
              </div>
            </form>
          </>
        )}
    </Modal>
  );
}
